import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, Mail, Eye, Edit, FileText, X } from 'lucide-react';
import * as Tabs from '@radix-ui/react-tabs';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { useNotifications } from '../../store/useStore';
import { generateLeadEmailHTML } from '../../lib/emailTemplate';
import { BRANDING } from '../../lib/branding';
import { supabase } from '../../lib/supabase';
import { leadService } from '../../lib/leadService';
import { Lead } from '../../types/leads.types';
import { SceltaModelloDialog } from '../email/modelli/SceltaModelloDialog';
import type { EmailTemplate } from '../../lib/email/templateService';
import { contestoLead, destinatarioDaLead } from '../../lib/email/renderContext';
import { renderPerDestinatario } from '@email/index.ts';

const emailSchema = z.object({
    email: z.string().email('Email non valida').min(1, 'Email richiesta'),
    subject: z.string().min(1, 'Soggetto richiesto'),
    body: z.string().min(1, 'Corpo email richiesto'),
});

type EmailFormData = z.infer<typeof emailSchema>;

interface SendLeadEmailModalProps {
    isOpen: boolean;
    onClose: () => void;
    lead: Lead;
    onSent?: () => void;
}

/**
 * Testi pronti per le email ai lead.
 *
 * La firma e l'oggetto usano il nome dell'azienda preso da `branding.ts`.
 * E' stato tolto il template "POSA": era di un altro progetto di Abruzzo.AI,
 * conteneva un numero di cellulare personale e un link a YouTube, ed era pure
 * quello proposto per primo all'utente.
 */
function templates(name: string): { key: string; label: string; subject: string; body: string }[] {
    const firma = `Team ${BRANDING.nomeBreve}`;
    return [
        {
            key: 'primo_contatto',
            label: 'Primo contatto',
            subject: `Grazie per il tuo interesse - ${BRANDING.nomeBreve}`,
            body: `Ciao ${name},

grazie per averci contattato. Abbiamo ricevuto la tua richiesta e saremo felici di aiutarti.

Quando avresti qualche minuto per una breve chiamata conoscitiva?

A presto,
${firma}`,
        },
        {
            key: 'follow_up',
            label: 'Follow-up',
            subject: `Ci risentiamo? - ${BRANDING.nomeBreve}`,
            body: `Ciao ${name},

volevo assicurarmi che tu abbia ricevuto le informazioni che ti abbiamo inviato.

Resto a disposizione per qualsiasi domanda o per fissare una call.

Cordiali saluti,
${firma}`,
        },
        {
            key: 'proposta',
            label: 'Invio proposta',
            subject: `La tua proposta personalizzata - ${BRANDING.nomeBreve}`,
            body: `Ciao ${name},

come promesso, ti inviamo la nostra proposta personalizzata.

Fammi sapere se hai bisogno di chiarimenti: possiamo organizzare una call quando preferisci.

Cordiali saluti,
${firma}`,
        },
    ];
}

export function SendLeadEmailModal({ isOpen, onClose, lead, onSent }: SendLeadEmailModalProps) {
    const { addNotification } = useNotifications();
    const [isSending, setIsSending] = useState(false);
    const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
    const [selectedTemplate, setSelectedTemplate] = useState('primo_contatto');

    const name = lead.full_name || lead.company_name || 'Cliente';
    const tpls = templates(name);

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<EmailFormData>({
        resolver: zodResolver(emailSchema),
        defaultValues: { email: lead.email || '', subject: '', body: '' },
    });

    // Il modello scelto, se si e' scelto di partire da uno invece che
    // scrivere a mano. Sono due modi diversi di comporre: o l'uno o l'altro.
    const [modello, setModello] = useState<EmailTemplate | null>(null);
    const [sceltaModelloAperta, setSceltaModelloAperta] = useState(false);

    const bodyValue = watch('body');
    const subjectValue = watch('subject');
    const previewHtml = generateLeadEmailHTML(bodyValue || '');

    useEffect(() => {
        if (isOpen) {
            const t = tpls[0];
            setSelectedTemplate(t.key);
            setValue('email', lead.email || '');
            setValue('subject', t.subject);
            setValue('body', t.body);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, lead.id]);

    /** Rende il modello con i dati di questo lead: e' la stessa resa che
     *  esce davvero, cosi' l'anteprima non racconta un'altra email. */
    const rendiModello = (t: EmailTemplate, oggetto: string) =>
        renderPerDestinatario(
            t.document,
            oggetto || t.subject,
            destinatarioDaLead(lead),
            contestoLead(),
            t.preheader ?? undefined,
        );

    const applicaModello = (t: EmailTemplate) => {
        const oggetto = subjectValue?.trim() ? subjectValue : t.subject;
        const reso = rendiModello(t, oggetto);
        setModello(t);
        setSelectedTemplate('');
        setValue('subject', oggetto);
        // Nel corpo finisce la versione testuale di quello che parte: e'
        // quella che resta scritta nella timeline del lead.
        setValue('body', reso.testo);
    };

    const togliModello = () => {
        setModello(null);
        const t = tpls[0];
        setSelectedTemplate(t.key);
        setValue('subject', t.subject);
        setValue('body', t.body);
    };

    const applyTemplate = (key: string) => {
        setModello(null);
        const t = tpls.find((x) => x.key === key);
        if (!t) return;
        setSelectedTemplate(key);
        setValue('subject', t.subject);
        setValue('body', t.body);
    };

    const onSubmit = async (data: EmailFormData) => {
        setIsSending(true);
        try {
            // Con un modello l'email e' quella impaginata a blocchi, resa con
            // lo stesso motore delle campagne. Senza, resta il testo semplice
            // dentro l'involucro di sempre.
            const reso = modello ? rendiModello(modello, data.subject) : null;
            const html = reso ? reso.html : generateLeadEmailHTML(data.body);
            const { data: res, error } = await supabase.functions.invoke('send-email', {
                body: {
                    to: data.email,
                    subject: reso ? reso.oggetto : data.subject,
                    text: reso ? reso.testo : data.body,
                    html,
                },
            });
            if (error) {
                const detail =
                    res && typeof res === 'object' && 'error' in res && typeof (res as { error: unknown }).error === 'string'
                        ? (res as { error: string }).error
                        : error.message;
                throw new Error(detail);
            }
            if (res && res.ok === false) throw new Error(res.error || 'Invio email fallito');

            await leadService.logEmailSent(lead.id, data.subject, data.body);

            // Dopo il primo contatto via email, avanza "nuovo" → "contattato"
            // (non arretriamo lead già più avanti in pipeline).
            if (lead.stage === 'nuovo') {
                try {
                    await leadService.updateStage(lead.id, 'contattato', 'nuovo');
                } catch (stageErr) {
                    console.error('Email inviata ma stage non aggiornato:', stageErr);
                }
            }

            addNotification({ type: 'success', title: 'Email inviata', message: `Inviata a ${data.email}` });
            onSent?.();
            reset();
            onClose();
        } catch (error) {
            console.error('Error sending lead email:', error);
            addNotification({
                type: 'error',
                title: 'Errore invio',
                message: error instanceof Error ? error.message : 'Impossibile inviare l\'email',
            });
        } finally {
            setIsSending(false);
        }
    };

    const handleClose = () => { if (!isSending) { reset(); setModello(null); onClose(); } };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Mail className="w-5 h-5" />
                        <span>Invia Email al Lead</span>
                    </DialogTitle>
                    <DialogDescription>
                        Inviata tramite lo SMTP configurato. Una copia arriva anche alla tua casella e l'invio viene registrato nella timeline del lead.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="lead-email">Destinatario <span className="text-red-500">*</span></Label>
                            <Input
                                id="lead-email"
                                type="email"
                                {...register('email')}
                                className={errors.email ? 'border-red-500' : ''}
                                disabled={isSending}
                            />
                            {errors.email && <p className="text-sm text-red-600">{errors.email.message}</p>}
                        </div>
                        <div className="space-y-2">
                            <Label>Testo pronto</Label>
                            <Select value={selectedTemplate} onValueChange={applyTemplate} disabled={isSending || !!modello}>
                                <SelectTrigger><SelectValue placeholder="Scegli un testo" /></SelectTrigger>
                                <SelectContent>
                                    {tpls.map((t) => (
                                        <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="lead-subject">Soggetto <span className="text-red-500">*</span></Label>
                        <Input
                            id="lead-subject"
                            {...register('subject')}
                            className={errors.subject ? 'border-red-500' : ''}
                            disabled={isSending}
                        />
                        {errors.subject && <p className="text-sm text-red-600">{errors.subject.message}</p>}
                    </div>

                    {modello ? (
                        <div className="space-y-2">
                            <div className="flex items-center justify-between gap-3">
                                <Label>Contenuto</Label>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={togliModello}
                                    disabled={isSending}
                                    className="h-auto py-1 text-xs text-neutral-500"
                                >
                                    <X className="w-3.5 h-3.5 mr-1.5" />
                                    Togli il modello e scrivi a mano
                                </Button>
                            </div>

                            <div className="rounded-lg border border-primary-200 bg-primary-50/40 px-3 py-2">
                                <p className="text-xs font-semibold text-gray-900">{modello.name}</p>
                                <p className="text-[11px] text-neutral-500 mt-0.5 leading-relaxed">
                                    Il contenuto arriva dal modello, gia' impaginato. Si cambia in
                                    Mailing &rarr; Modelli: qui puoi ancora modificare l'oggetto.
                                </p>
                            </div>

                            <div className="border rounded-lg overflow-hidden bg-gray-100">
                                <iframe
                                    srcDoc={rendiModello(modello, subjectValue).html}
                                    className="w-full border-0 block bg-gray-100"
                                    style={{ height: 420 }}
                                    title="Anteprima del modello"
                                    sandbox="allow-same-origin"
                                />
                            </div>
                        </div>
                    ) : (
                    <div className="space-y-2">
                        <Label htmlFor="lead-body">Corpo Email <span className="text-red-500">*</span></Label>
                        <Tabs.Root value={activeTab} onValueChange={(v) => setActiveTab(v as 'edit' | 'preview')}>
                            <Tabs.List className="inline-flex h-10 items-center justify-center rounded-lg bg-gray-100 p-1 mb-2">
                                <Tabs.Trigger value="edit" className="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">
                                    <Edit className="w-4 h-4 mr-2" /> Editor
                                </Tabs.Trigger>
                                <Tabs.Trigger value="preview" className="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium data-[state=active]:bg-white data-[state=active]:shadow-sm">
                                    <Eye className="w-4 h-4 mr-2" /> Anteprima
                                </Tabs.Trigger>
                            </Tabs.List>

                            <Tabs.Content value="edit" className="mt-0">
                                <Textarea
                                    id="lead-body"
                                    rows={10}
                                    {...register('body')}
                                    className={errors.body ? 'border-red-500' : ''}
                                    disabled={isSending}
                                />
                                {errors.body && <p className="text-sm text-red-600 mt-1">{errors.body.message}</p>}
                            </Tabs.Content>

                            <Tabs.Content value="preview" className="mt-0">
                                <div className="border rounded-lg overflow-hidden bg-white">
                                    <div className="bg-gray-50 border-b px-3 sm:px-4 py-2 flex flex-wrap items-center justify-between gap-1">
                                        <p className="text-xs font-medium text-gray-600">Anteprima Email</p>
                                        <p className="text-xs text-gray-500 truncate max-w-full">Soggetto: {subjectValue || '(vuoto)'}</p>
                                    </div>
                                    <div className="overflow-auto bg-gray-100 p-2 sm:p-4" style={{ maxHeight: '450px' }}>
                                        {bodyValue ? (
                                            <div className="bg-white rounded shadow-sm" style={{ maxWidth: '600px', margin: '0 auto' }}>
                                                <iframe
                                                    srcDoc={previewHtml}
                                                    className="w-full border-0"
                                                    style={{ minHeight: '520px', display: 'block' }}
                                                    title="Anteprima email"
                                                    sandbox="allow-same-origin"
                                                />
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-center h-[400px] text-gray-400">
                                                Scrivi qualcosa per vedere l'anteprima
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Tabs.Content>
                        </Tabs.Root>
                    </div>
                    )}

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setSceltaModelloAperta(true)}
                            disabled={isSending}
                            className="sm:mr-auto"
                        >
                            <FileText className="w-4 h-4 mr-2" />
                            {modello ? 'Cambia modello' : 'Usa un modello'}
                        </Button>
                        <Button type="button" variant="outline" onClick={handleClose} disabled={isSending}>Annulla</Button>
                        <Button type="submit" disabled={isSending}>
                            {isSending ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio in corso...</>
                            ) : (
                                <><Mail className="w-4 h-4 mr-2" /> Invia Email</>
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>

            <SceltaModelloDialog
                aperto={sceltaModelloAperta}
                onChiudi={setSceltaModelloAperta}
                ambito="lead"
                sovrascrive
                onScelto={applicaModello}
            />
        </Dialog>
    );
}

export default SendLeadEmailModal;
